"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { Input, toast } from "@instacover/ui-web"
import { useTranslations } from "next-intl"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { requestSignupInstructions } from "@/app/[locale]/auth/actions"
import { Heading } from "@/components/Heading"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
  FormWrapper,
} from "@/components/shadcn/Form"
import { Spacer } from "@/components/Spacer"
import { Text } from "@/components/Text"
import { urls } from "@/constants/urls"
import { Link } from "@/utils/navigation"

const formSchema = z.object({
  email: z.email(),
})

export default function Page() {
  const t = useTranslations("auth")
  const tc = useTranslations("common")

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "" },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const res = await requestSignupInstructions({ email: values.email })
    if (res?.data?.error.type === "emailNotFound") {
      toast.error(t("userDoesNotExistTitle"))
      return
    }

    if (res?.data?.error.type === "userAlreadySignedUp") {
      toast.error(t("userAlreadySignedUpTitle"))
      return
    }

    if (typeof res?.data?.error === "string") {
      toast.error(res.data?.error)
      return
    }

    if (res?.serverError || res?.validationErrors) {
      toast.error(tc("error"), { description: tc("errorDescription") })
      return
    }
  }

  return (
    <>
      <Heading order={1} size={2} className="text-center">
        {t("signUpHeading")}
      </Heading>

      <Spacer className="h-10" />

      <p className="text-center text-sm leading-relaxed text-slate-500">{t("signUpText")}</p>

      <Spacer className="h-10" />

      <FormWrapper {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            name="email"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("emailLabel")}</FormLabel>

                <FormControl>
                  <Input {...field} type="email" autoComplete="email" placeholder={t("emailPlaceholder")} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <Spacer className="h-10" />

          <FormSubmitButton className="w-full" size="sm">
            {t("signUpSubmitButton")}
          </FormSubmitButton>
        </form>
      </FormWrapper>

      <Spacer className="h-4" />

      <div className="flex justify-center gap-4">
        <Link href={urls.auth.signin}>
          <Text size="sm" variant="link">
            {t("signUpBackToSignIn")}
          </Text>
        </Link>
      </div>
    </>
  )
}
